import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ApiService } from 'src/app/components/shared/api.service';
import { localStorageService } from 'src/app/components/shared/localstorage.service';
import { MainService } from 'src/app/components/shared/main.service';
import { PasswordStrengthService, PasswordSpaceValidator } from 'src/app/components/shared/password-strength.service';
import { passwordValidator, SharedService } from 'src/app/components/shared/shared.service';
import { PermissionsService } from 'src/app/components/shared/permissions/permissions.service';
import { NgClass } from '@angular/common';

@Component({
    selector: 'app-force-password-change',
    templateUrl: './force-password-change.component.html',
    styleUrl: './force-password-change.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, ReactiveFormsModule, NgClass, RouterLink]
})
export class ForcePasswordChangeComponent implements OnInit {
  public passwordForm: UntypedFormGroup;
  hasUpperCase = false;
  hasMaxChar = false;
  hasNumber = false;
  hasSpecialChar = false;
  hideCurrentPwd = false;
  hidePwd = false;
  hideConfirmPwd = false;
  showPassTooltip = false;

  constructor(private _fb: UntypedFormBuilder, private _api: ApiService, private _local: localStorageService,
    public _shared: SharedService, private _router: Router, private main: MainService,
    private passwordStrengthService: PasswordStrengthService, private _permissions: PermissionsService) {
    this.passwordForm = this._fb.group({
      current_password: ['', Validators.required],
      new_password: ['', [Validators.required, Validators.maxLength(50), passwordValidator(), PasswordSpaceValidator.cannotContainSpace]],
      confirm_password: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    if (!this._local.customGetItem('token')) {
      this._router.navigate(['login']);
      return;
    }
    this.passwordForm.get('new_password')?.valueChanges.subscribe(password => {
      this.hasUpperCase = (/[A-Z]/.test(password)) && (/[a-z]/.test(password));
      this.hasNumber = /[0-9]/.test(password);
      this.hasSpecialChar = /[^A-Za-z0-9]/.test(password);
      this.hasMaxChar = password.length >= 8;
    });
  }

  submit() {
    const errorMsg = 'Password must contain at least 8 characters, including 1 uppercase, 1 lowercase, 1 number, and 1 special character. Spaces are not allowed.';
    if (this.passwordForm.controls['current_password'].value.trim() == '') {
      this._shared.customPopups('Please enter your current password', 2);
      return;
    }
    if (this.passwordForm.controls['new_password'].errors?.['cannotContainSpace']) {
      this._shared.customPopups(errorMsg, 2);
      return;
    }
    if (this.passwordForm.value.new_password != this.passwordForm.value.confirm_password) {
      this._shared.customPopups('Passwords did not match', 2);
      return;
    }
    if (this.passwordForm.value.new_password.length < 8
      || !/[A-Z]/.test(this.passwordForm.value.new_password)
      || !/[a-z]/.test(this.passwordForm.value.new_password)
      || !/[0-9]/.test(this.passwordForm.value.new_password)
      || !/[^A-Za-z0-9]/.test(this.passwordForm.value.new_password)) {
      this._shared.customPopups(errorMsg, 2);
      return;
    }
    if (this.passwordForm.value.new_password == this.passwordForm.value.current_password) {
      this._shared.customPopups('New password cannot be the same as the old password.', 2);
      return;
    }

    const admin_profile = this._local.customGetItem('admin_profile') || {};
    const data = {
      admin_email: admin_profile.admin_email,
      old_password: this._shared.crypto(this.passwordForm.value.current_password),
      new_password: this._shared.crypto(this.passwordForm.value.new_password),
      reset_password_token: 'd3n3fIt5'
    };

    this._api.api(data, 'auth/change_password').subscribe(response => {
      if (!response.is_error) {
        this._local.customRemoveItem('password_expired');
        this._local.customSetItem('token', response.access_token);
        this._local.customSetItem('admin_profile', response.admin_profile ? response.admin_profile[0] : {});
        // Mirrors login.component.ts's post-login sequence: change_password's
        // response carries menu/capabilities the same way email_login_v2's
        // does (both come from fetchAdminLoginData), so hydrate permissions
        // and the menu from THIS response before routing -- not a separate
        // get_settings round-trip, which login doesn't do either.
        this._permissions.setFromLoginResponse(response);
        this.main.fetchAdminList();
        if (response.menu && response.menu.length) {
          this.main.menu$.next(response.menu);
        }
        this._shared.customPopups('Password Changed Successfully', 0);
        this._shared.dashboardMenuExist(response.menu).then(res => {
          const defaultSalesView = this._permissions.isManager ? 'L1' : 'my';
          this._local.customSetItem('sales_dashboard_view', defaultSalesView);
          const dashboards = this._permissions.dashboards;
          const hasAnySalesAccess = dashboards.l1 || dashboards.l2 || dashboards.l3 || dashboards.l4
            || this._permissions.isAgent
            || this._permissions.isTeamLead
            || this._permissions.isManager;
          const firstMenuPath = response.menu && response.menu.length ? response.menu[0]['path'] : null;
          if ((res && (!hasAnySalesAccess || this._permissions.isWildcard)) || !firstMenuPath) {
            this._router.navigate(['app', 'dashboard']);
          } else {
            this._router.navigate([firstMenuPath]);
          }
        });
      } else {
        this._shared.customPopups(response.original_text || 'Current Password is incorrect.', 1);
      }
    });
  }

  logout() {
    this._local.customClearAllItems();
    this._router.navigate(['login']);
  }
}
